import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import {
  ActionsDialogue, Bouton, Carte, Champ, Dialogue, GrilleFormulaire,
  ICONES_MODULE, IconeCroix, IconeDeconnexion, IconeMenu,
  LIBELLES_THEMES, Message, ReglageZoom,
  NOMS_THEMES, RUPTURES, useMediaQuery, useTheme, type NomTheme,
} from '@journal/ui';
import { messageErreur, useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useFonctionnalite } from '../lib/droits.tsx';
import { BandeauInfo } from './BandeauInfo.tsx';
import { BandeauImitation } from './BandeauImitation.tsx';
import { BandeauSieges } from './BandeauSieges.tsx';
import { classeChoisie, SelecteurClasse } from './SelecteurClasse.tsx';
import { Abonnement } from '../pages/Abonnement.tsx';

/**
 * Coquille applicative.
 *
 * ── Barre latérale, et non onglets horizontaux ──────────────────────────────
 * Onze sections ne tiennent pas sur une rangée : elles passaient à la ligne, et
 * l'en-tête occupait alors le quart de la hauteur utile. Une colonne latérale
 * les accueille toutes, les garde visibles en permanence, et laisse le contenu
 * occuper la largeur restante — c'est la disposition de tout outil de travail
 * sur ordinateur.
 *
 * Sur mobile, la colonne devient un panneau qui se recouvre : la place n'y est
 * pas, et une colonne réduite à des icônes seules oblige à deviner.
 *
 * ── Les onglets sont des liens ──────────────────────────────────────────────
 * Ils changent l'adresse. Un `<button>` qui navigue casse le clic du milieu,
 * l'ouverture dans un nouvel onglet, la copie d'adresse et le bouton Retour —
 * quatre gestes qu'on fait sans y penser, et dont l'absence passe pour une
 * panne.
 */

/*
 * « Programme » ne figure plus ici.
 *
 * Consulter le référentiel officiel en entier n'est pas un geste
 * d'enseignant : il rencontre le programme LÀ où il travaille — en
 * programmation, en progressions, en évaluation, où les notions lui sont
 * proposées filtrées par le niveau de sa classe. Un module qui déroule les
 * trois cycles ajoutait une porte vers un catalogue, pas vers son travail.
 *
 * La route reste servie : un signet existant continue de fonctionner, et
 * l'écran demeure utile à qui veut vérifier ce que porte une édition.
 */
/**
 * `large` : cet écran prend TOUTE la largeur disponible.
 *
 * ── Deux besoins qu'on traitait pareil, à tort ──────────────────────────────
 * La coquille plafonnait tout le contenu à 84 rem. C'est une largeur de
 * LECTURE : au-delà, l'œil perd la ligne en revenant à la marge, et un
 * formulaire ou un texte y gagnent vraiment.
 *
 * Un tableau ne se lit pas ainsi. On y compare des colonnes, on balaie une
 * semaine du regard — la largeur n'y fatigue pas, elle sert. Mesuré sur la
 * progression avant correction : sur un écran de 1920 px, le tableau recevait
 * 1238 px et en demandait 1323. Il défilait horizontalement sur un grand écran,
 * pendant que 682 px restaient vides — dont 336 pour ce seul plafond.
 *
 * Les écrans d'administration gardent le plafond : ce sont des formulaires.
 */
export const MODULES = [
  { id: 'accueil', chemin: '/', libelle: 'Accueil' },
  { id: 'journal', chemin: '/journal', libelle: 'Cahier journal', large: true },
  { id: 'fiches', chemin: '/fiches-preparation', libelle: 'Fiche de préparation', large: true },
  { id: 'edt', chemin: '/emploi-du-temps', libelle: 'Emploi du temps', large: true },
  { id: 'progressions', chemin: '/progressions', libelle: 'Progressions', large: true },
  { id: 'programmation', chemin: '/programmation', libelle: 'Programmation', large: true },
  { id: 'evaluation', chemin: '/evaluation', libelle: 'Évaluation', large: true },
] as const;

/**
 * Annexe : ce qui accompagne la classe sans en être le cœur.
 *
 * L'agenda et les 108 heures ne décrivent ni séances ni élèves : l'un est un
 * carnet de notes libres, l'autre le décompte des obligations de service. Les
 * ranger à part, juste avant l'administration, laisse la liste principale aux
 * écrans qu'on ouvre pour faire classe.
 */
export const ANNEXE = [
  { id: 'agenda', chemin: '/agenda', libelle: 'Agenda', large: true },
  { id: 'heures', chemin: '/heures', libelle: '108 heures', large: true },
  /*
   * Impressions : le dossier de la classe, tous modules confondus. En annexe,
   * parce qu'on ne l'ouvre pas pour faire classe mais pour archiver ou
   * présenter — en fin de période, avant une inspection.
   */
  { id: 'impressions', chemin: '/impressions', libelle: 'Impressions', large: true },
] as const;

/**
 * Cet écran doit-il prendre toute la largeur ?
 *
 * Décidé à partir du CHEMIN, et non d'une propriété posée par la page : la
 * coquille est le parent, elle ne peut pas lire ce que son enfant voudrait.
 * Passer par un contexte marcherait, mais ferait dépendre la mise en page d'un
 * effet de rendu — donc d'un saut de largeur visible au premier affichage.
 */
/**
 * Les initiales affichées dans la pastille de la barre latérale.
 *
 * Deux lettres au plus : « Claire Martin » donne CM. Un compte sans nom
 * retombe sur son adresse, dont on prend la première lettre — jamais rien,
 * qui laisserait une pastille vide à côté d'un nom.
 */
export function initiales(nomAffiche: string): string {
  const mots = nomAffiche.trim().split(/[\s@.]+/).filter((m) => m.length > 0);
  if (mots.length === 0) return '?';
  const premieres = mots.slice(0, 2).map((m) => m[0]!.toUpperCase());
  return premieres.join('');
}

/**
 * L'identité de la session, en pied de barre latérale.
 *
 * Extraite : elle a sa propre logique d'affichage — la pastille, le repli sur
 * l'adresse, le comportement en barre réduite — et la coquille est déjà longue.
 *
 * ── Cliquable, quand on a le droit de se renommer ──────────────────────────
 * Un enseignant seul est propriétaire de son espace : il corrige son nom ici,
 * là où il le lit. Un membre d'une école ne le peut pas — l'annuaire est tenu
 * par la direction, comme la répartition des classes — et le bloc reste alors
 * un simple affichage. Offrir un bouton qui répondrait 403 use la confiance
 * dans l'interface plus qu'il ne rend service.
 */
function IdentiteConnectee({
  nom, email, replie, modifiable, surModification,
}: {
  nom: string;
  email: string;
  replie: boolean;
  modifiable: boolean;
  surModification: () => void;
}) {
  const contenu = (
    <>
      <span
        aria-hidden="true"
        className={[
          'flex size-6 shrink-0 items-center justify-center rounded-full',
          'bg-[var(--color-primary-soft)] text-[11px] font-semibold',
          'text-[var(--color-primary)]',
        ].join(' ')}
      >
        {initiales(nom)}
      </span>
      <span className={replie ? 'hidden' : 'min-w-0 text-left'}>
        <span className="block truncate text-[13px] font-medium">{nom}</span>
        {/* L'adresse en dessous, tue quand elle FAIT le nom : deux fois la
            même ligne se lit comme un défaut d'affichage. */}
        {nom !== email && (
          <span className="block truncate text-[11px] text-[var(--color-fg-muted)]">
            {email}
          </span>
        )}
      </span>
    </>
  );

  // L'infobulle porte les deux, y compris quand la barre est réduite à ses
  // icônes : c'est alors le seul moyen de savoir qui est connecté.
  const infobulle = nom === email ? email : `${nom} · ${email}`;
  const disposition = [
    'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5',
    replie ? 'justify-center' : '',
  ].join(' ');

  if (!modifiable) {
    return <div title={infobulle} className={disposition}>{contenu}</div>;
  }

  return (
    <button
      type="button"
      onClick={surModification}
      title={`${infobulle} (modifier mon nom)`}
      aria-label={`Modifier mon nom, actuellement ${nom}`}
      className={[
        disposition,
        'transition-colors duration-150 hover:bg-[var(--color-surface-hover)]',
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[var(--color-focus)]',
      ].join(' ')}
    >
      {contenu}
    </button>
  );
}

/**
 * Corriger son propre nom, sans quitter l'écran où on le lit.
 *
 * Deux champs et rien d'autre : ce n'est pas un écran de profil, c'est la
 * correction d'une coquille — un prénom mal saisi à l'inscription se voit tous
 * les jours en bas de la barre, et il n'existait aucun endroit pour le
 * reprendre.
 */
function DialogueMonNom({
  ouvert, prenomInitial, nomInitial, surFermeture, surEnregistrement,
}: {
  ouvert: boolean;
  prenomInitial: string;
  nomInitial: string;
  surFermeture: () => void;
  surEnregistrement: (prenom: string, nom: string) => Promise<void>;
}) {
  const [prenom, setPrenom] = useState(prenomInitial);
  const [nom, setNom] = useState(nomInitial);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // La saisie repart de l'enregistré à chaque ouverture : une correction
  // abandonnée ne doit pas ressortir à la suivante.
  useEffect(() => {
    if (!ouvert) return;
    setPrenom(prenomInitial);
    setNom(nomInitial);
    setErreur(null);
  }, [ouvert, prenomInitial, nomInitial]);

  async function enregistrer() {
    setErreur(null);
    setEnCours(true);
    try {
      await surEnregistrement(prenom.trim(), nom.trim());
      surFermeture();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Dialogue titre="Mon nom" ouvert={ouvert} surFermeture={surFermeture}>
      <div className="flex flex-col gap-4">
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <GrilleFormulaire>
          <Champ
            libelle="Prénom"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            autoComplete="given-name"
            maxLength={60}
            required
          />
          <Champ
            libelle="Nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            autoComplete="family-name"
            maxLength={60}
            required
          />
        </GrilleFormulaire>
        <ActionsDialogue>
          <Bouton variante="secondary" onClick={surFermeture}>Annuler</Bouton>
          <Bouton
            onClick={() => void enregistrer()}
            disabled={enCours || prenom.trim() === '' || nom.trim() === ''}
          >
            Enregistrer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}

export function ecranLarge(chemin: string): boolean {
  return [...MODULES, ...ANNEXE].some((m) => 'large' in m && m.large
    && (chemin === m.chemin || chemin.startsWith(`${m.chemin}/`)));
}

/** Clé du repli de la barre, mémorisé par poste de travail. */
const CLE_REPLI = 'journal.barre-repliee';

/** Séparés du quotidien : ce sont des réglages, consultés rarement. */
const ADMINISTRATION = [
  { id: 'reglages', chemin: '/reglages', libelle: 'Réglages' },
  { id: 'equipe', chemin: '/equipe', libelle: 'Équipe' },
  { id: 'securite', chemin: '/securite', libelle: 'Sécurité' },
  { id: 'abonnement', chemin: '/abonnement', libelle: 'Abonnement' },
] as const;

export type IdModule =
  | (typeof MODULES)[number]['id']
  | (typeof ANNEXE)[number]['id']
  | (typeof ADMINISTRATION)[number]['id'];

/** Le titre d'une section de la barre latérale. */
function TitreSection({ replie, children }: { replie: boolean; children: string }) {
  // Replié, le titre déborderait ou se couperait en « Adm… ». Une simple
  // séparation dit la même chose sans rien tronquer.
  if (replie) return <hr aria-hidden="true" className="my-3 border-[var(--color-border)]" />;
  return (
    <p className="mb-1 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-fg-muted)]">
      {children}
    </p>
  );
}

function LienModule({
  module, surNavigation, replie = false,
}: {
  module: { id: string; chemin: string; libelle: string };
  surNavigation: () => void;
  /** Barre repliée : l'icône seule, l'intitulé passe dans le nom accessible. */
  replie?: boolean;
}) {
  const Icone = ICONES_MODULE[module.id];
  return (
    <li>
      <NavLink
        to={module.chemin}
        // `end` sur « / » : sans lui, l'entrée Accueil resterait marquée active
        // sur TOUTES les pages, puisque toute adresse commence par « / ».
        end={module.chemin === '/'}
        onClick={surNavigation}
        /*
         * L'intitulé reste porté par le lien même quand il n'est pas dessiné.
         *
         * Une icône seule ne dit rien à un lecteur d'écran, et un enseignant
         * qui replie le menu pour gagner de la place ne doit pas y perdre la
         * navigation. `title` sert la souris, `aria-label` le clavier et la
         * synthèse vocale.
         */
        {...(replie ? { title: module.libelle, 'aria-label': module.libelle } : {})}
        className={({ isActive }) => [
          'relative flex items-center gap-2.5 rounded-lg py-2',
          replie ? 'px-2 lg:justify-center' : 'px-2.5',
          'text-[13px] font-medium transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-[var(--color-focus)] focus-visible:ring-offset-1',
          'focus-visible:ring-offset-[var(--color-surface)]',
          isActive
            // Fond teinté ET barre verticale : la couleur seule ne renseigne
            // pas un daltonien, et `aria-current` s'en charge pour le reste.
            ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-fg)] '
              + 'before:absolute before:left-0 before:top-1.5 before:bottom-1.5 '
              + 'before:w-[3px] before:rounded-r-full before:bg-[var(--color-primary)]'
            : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] '
              + 'hover:text-[var(--color-fg)]',
        ].join(' ')}
      >
        {Icone && <Icone taille={17} />}
        <span className={replie ? 'truncate lg:hidden' : 'truncate'}>{module.libelle}</span>
      </NavLink>
    </li>
  );
}

function ReglagesAffichage() {
  const { theme, definir } = useTheme();

  const classes = [
    'w-full cursor-pointer appearance-none rounded-md border px-2 py-1',
    'border-[var(--color-border)] bg-[var(--color-surface-input)]',
    'text-[11px] text-[var(--color-fg-muted)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]',
  ].join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)]">
          Thème
        </span>
        <select
          value={theme}
          onChange={(e) => definir(e.target.value as NomTheme)}
          className={classes}
        >
          {/* Deux entrées, et plus de troisième. « Système » n'était pas un
              thème mais un défaut : l'application ouvre désormais toujours en
              clair, et ce choix-ci se mémorise par poste de travail. */}
          {NOMS_THEMES.map((n) => (
            <option key={n} value={n}>{LIBELLES_THEMES[n]}</option>
          ))}
        </select>
      </label>

    </div>
  );
}

export function Coquille() {
  const { api, deconnecter } = useSession();
  const annee = useAnneeActive();
  const equipeIncluse = useFonctionnalite('equipe');
  /*
   * « A-t-il une classe ? » se lit sur les classes DONT IL EST TITULAIRE.
   *
   * `/classrooms` renvoie à un responsable toutes celles de l'école : s'en
   * servir tel quel rendrait les modules à un directeur qui n'enseigne pas,
   * précisément ce qu'on veut éviter. Vrai tant que la réponse n'est pas
   * arrivée, comme pour les fonctionnalités d'offre : masquer par défaut
   * ferait clignoter le menu à chaque chargement.
   */
  const mesClasses = useDonnees<Array<{ id: string; teacherId: string | null }>>('/classrooms');
  const moi = useDonnees<{
    id: string; tenantSlug: string; tenantName: string; role: string;
    email: string; firstName: string; lastName: string; displayName: string;
    impersonation?: boolean;
  }>('/auth/me');
  /*
   * ── « A-t-on une classe de travail ? » ───────────────────────────────────
   * Titulaire d'une classe, OU en ayant ouvert une — ce que fait un directeur
   * qui va voir le cahier journal de son école. Ne compter que les siennes lui
   * laissait l'écran d'administration alors qu'il venait de choisir une classe
   * dans le sélecteur juste au-dessus.
   *
   * `/classrooms` ne rend que les classes accessibles : une valeur périmée
   * dans le stockage local n'ouvre donc rien.
   */
  const choisie = classeChoisie();
  const aUneClasse = !mesClasses.donnees || !moi.donnees
    || mesClasses.donnees.some((c) => c.teacherId === moi.donnees!.id)
    || mesClasses.donnees.some((c) => c.id === choisie)
    // Le SUPPORT n'est titulaire d'aucune classe : sans cela, il voyait
    // l'écran de direction au lieu de l'espace qu'il vient consulter.
    || (moi.donnees.impersonation === true && mesClasses.donnees.length > 0);
  // Les niveaux servent uniquement au pied de page d'impression : ils ne sont
  // pas affichés à l'écran, où la barre latérale dit déjà quelle année est
  // ouverte.
  const reglages = useDonnees<Record<string, string>>('/settings');

  /*
   * Mauvaise adresse : on le dit, on ne casse pas en silence.
   *
   * Chaque école a son sous-domaine. Un compte qui se connecte sur le
   * sous-domaine d'une AUTRE école obtient bien un jeton, puis se voit refuser
   * toutes les données métier par un 403 : l'application paraît alors cassée,
   * et l'utilisateur conclut que son mot de passe ne marche pas. C'est
   * exactement ce qui s'est produit.
   *
   * ── L'adresse GÉNÉRIQUE ne passe plus par ici ────────────────────────────
   * Elle y passait, et c'était le cas le plus fréquent : un signet sur
   * l'adresse du produit, un lien partagé, la page de connexion elle-même.
   * Le serveur y résout désormais l'espace depuis le jeton, et l'on arrive
   * chez soi sans avoir à connaître son sous-domaine par cœur.
   *
   * Cet écran reste pour ce qu'il désigne vraiment : une adresse qui nomme
   * l'école de quelqu'un d'autre.
   *
   * 403 et non 404 : un espace neuf sans année scolaire répond 404, ce qui est
   * normal et n'a rien à voir.
   */
  const mauvaisEspace = mesClasses.statutErreur === 403 && !!moi.donnees;

  /*
   * ── L'espace en cours de suppression ──────────────────────────────────────
   * Un espace supprimé n'est plus RÉSOLU : toute donnée métier revient en 400,
   * exactement comme une adresse inconnue. L'application affichait donc une
   * erreur technique à quelqu'un qui venait précisément se raviser — alors que
   * le délai de grâce de trente jours existe pour cela, et que le courriel de
   * confirmation lui promet noir sur blanc de pouvoir annuler « en vous
   * reconnectant ».
   *
   * La lecture n'est tentée QUE sur ce 400 : la faire à chaque chargement
   * coûterait une requête par ouverture, chez tout le monde, pour un cas qui
   * n'arrive presque jamais.
   */
  const suppression = useDonnees<{ purgeAfter: string | null; peutAnnuler: boolean }>(
    mesClasses.statutErreur === 400 ? '/rgpd/deletion/status' : null,
    [mesClasses.statutErreur],
  );
  const suppressionEnCours = suppression.donnees?.purgeAfter ?? null;
  const niveaux = (reglages.donnees?.niveaux ?? '')
    .split(',').map((n) => n.trim().toUpperCase()).filter(Boolean)
    .join(' – ');
  const emplacement = useLocation();
  const [panneauOuvert, setPanneauOuvert] = useState(false);

  /*
   * ── Barre repliée : le seul gain qui compte sur un petit écran ────────────
   *
   * Le plafond de largeur ne mord pas sur un portable d'école de 1366 px — il
   * n'y a rien à récupérer de ce côté. La barre latérale, elle, y prend 240 px
   * sur 1356, soit près d'un cinquième de l'écran, en permanence, pour une
   * navigation qu'on utilise quelques secondes par heure.
   *
   * Repliée, elle n'en occupe plus que 56 : les icônes restent, les intitulés
   * partent dans l'infobulle et le nom accessible. Le choix se mémorise par
   * poste de travail — c'est un réglage de confort, pas une préférence de
   * compte, et il n'a pas à suivre l'enseignant d'un ordinateur à l'autre.
   *
   * Le repli ne vaut qu'à partir de 1024 px : en dessous, la barre est déjà un
   * tiroir qui ne prend aucune place.
   */
  const [barreRepliee, setBarreRepliee] = useState(() => {
    try {
      return globalThis.localStorage?.getItem(CLE_REPLI) === '1';
    } catch {
      // Navigation privée, stockage refusé : on déplie, ce qui est l'état le
      // plus explicite pour qui découvre l'application.
      return false;
    }
  });

  /*
   * ── Trois mises en page, et non deux ──────────────────────────────────────
   *
   * Sous 768 px : la barre est un TIROIR par-dessus le contenu. Elle ne lui
   * prend rien, et se referme à chaque navigation.
   *
   * De 768 à 1023 px — la tablette en portrait : elle tient en RAIL de 56 px,
   * icônes seules, toujours visible. Cette bande recevait jusqu'ici la mise en
   * page du téléphone : il fallait ouvrir un tiroir pour changer d'écran, sur
   * une surface qui avait pourtant la place d'afficher la navigation en
   * permanence.
   *
   * Au-delà de 1024 px : la barre obéit au choix de l'enseignant, dépliée ou
   * repliée.
   *
   * Le rail n'écrase pas ce choix, il s'y substitue le temps de la bande : au
   * retour sur grand écran, la barre reprend l'état demandé.
   */
  const tablette = useMediaQuery(RUPTURES.tablette);
  const bureau = useMediaQuery(RUPTURES.bureau);
  const enRail = tablette && !bureau;
  const replieEffectif = enRail || (bureau && barreRepliee);
  const barreVisible = tablette || panneauOuvert;

  const basculerBarre = () => setBarreRepliee((replie) => {
    try {
      globalThis.localStorage?.setItem(CLE_REPLI, replie ? '0' : '1');
    } catch { /* le réglage ne survivra pas au rechargement, sans plus */ }
    return !replie;
  });

  // Le panneau mobile se referme à chaque navigation : le laisser ouvert
  // masquerait l'écran qu'on vient d'atteindre.
  useEffect(() => setPanneauOuvert(false), [emplacement.pathname]);

  const fermer = () => setPanneauOuvert(false);

  /*
   * Le droit de se renommer suit celui de tenir l'annuaire.
   *
   * Enseignant seul : propriétaire de son espace, il corrige son nom ici.
   * École : la direction tient l'annuaire, comme la répartition des classes,
   * et un membre s'adresse à elle.
   */
  const [nomOuvert, setNomOuvert] = useState(false);
  const peutSeRenommer = moi.donnees?.role === 'OWNER' || moi.donnees?.role === 'ADMIN';

  if (suppressionEnCours) {
    return (
      <ReactivationEspace
        purgeAfter={suppressionEnCours}
        peutAnnuler={suppression.donnees?.peutAnnuler ?? false}
      />
    );
  }

  /*
   * ── Abonnement non réglé ─────────────────────────────────────────────────
   * Le serveur répond 402 à toute donnée métier : la facturation et l'export,
   * rien d'autre, jusqu'au paiement.
   */
  if (mesClasses.statutErreur === 402) {
    return (
      <AccesSuspendu
        direction={moi.donnees?.role === 'OWNER' || moi.donnees?.role === 'ADMIN'}
      />
    );
  }

  if (mauvaisEspace) {
    const port = globalThis.location?.port ? `:${globalThis.location.port}` : '';
    const domaine = (globalThis.location?.hostname ?? '').split('.').slice(-1)[0] || 'localhost';
    const bonneAdresse =
      `${globalThis.location?.protocol ?? 'http:'}//${moi.donnees!.tenantSlug}.${domaine}${port}/`;
    return (
      <main id="contenu" className="mx-auto max-w-[38rem] px-5 py-16">
        <Carte titre="Vous n’êtes pas à la bonne adresse">
          <p className="text-[14px] leading-relaxed">
            Votre compte appartient à l’espace
            {` « ${moi.donnees!.tenantName} » `}
            et ne peut être utilisé que depuis son adresse. Vos identifiants
            sont corrects : c’est l’adresse qui ne l’est pas.
          </p>
          <p className="mt-4">
            <a
              href={bonneAdresse}
              className="inline-flex h-[38px] items-center rounded-lg bg-[var(--color-primary)] px-4 text-[13px] font-medium text-[var(--color-primary-fg)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
            >
              Aller à mon espace
            </a>
          </p>
          <p className="mt-3 break-all text-[12px] text-[var(--color-fg-muted)]">
            {bonneAdresse}
          </p>
        </Carte>
      </main>
    );
  }

  return (
    <div className="min-h-dvh bg-[var(--color-bg)] text-[var(--color-fg)]">
      {/* Lien d'évitement : première tabulation de la page, il permet aux
          utilisateurs au clavier de sauter la navigation. */}
      <a
        href="#contenu"
        className={[
          'sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50',
          'focus:rounded-lg focus:bg-[var(--color-surface)] focus:px-4 focus:py-2',
          'focus:text-sm focus:font-medium focus:shadow-[var(--ombre-flottante)]',
          'focus:outline-2 focus:outline-[var(--color-focus)]',
        ].join(' ')}
      >
        Aller au contenu
      </a>

      {/* ── Barre latérale ────────────────────────────────────────────────
          Fixe sur grand écran, panneau glissant en dessous de 1024 px. */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 flex flex-col',
          'border-r border-[var(--color-border)] bg-[var(--color-surface)]',
          'transition-transform duration-200 print:hidden',
          replieEffectif ? 'w-14' : 'w-60',
          barreVisible ? 'translate-x-0' : '-translate-x-full',
          // L'ombre marque le tiroir qui FLOTTE au-dessus du contenu. Posée sur
          // le rail, elle le détacherait d'une page dont il fait partie.
          panneauOuvert && !tablette ? 'shadow-[var(--ombre-flottante)]' : '',
        ].join(' ')}
      >
        <div
          className={[
            'flex items-center gap-2.5 border-b border-[var(--color-border)] py-3.5',
            replieEffectif ? 'justify-center px-2' : 'px-4',
          ].join(' ')}
        >
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--color-primary)] text-sm font-bold text-[var(--color-primary-fg)] shadow-[var(--ombre-bouton)]"
          >
            J
          </span>
          <div className={replieEffectif ? 'hidden' : 'min-w-0 flex-1'}>
            <p className="truncate text-[13px] font-semibold leading-tight">
              Journal de Classe
            </p>
            {/* L'année active, en permanence sous les yeux : une application
                multi-années où l'on ne voit pas laquelle est ouverte fait
                saisir dans la mauvaise. */}
            <p className="truncate text-[11px] leading-tight text-[var(--color-fg-muted)]">
              {annee.donnees?.label ?? 'Aucune année active'}
            </p>
          </div>
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer le menu"
            className={[
              'rounded-md p-1 text-[var(--color-fg-muted)]',
              'hover:bg-[var(--color-surface-hover)]',
              // Fermer n'a de sens que pour le tiroir : le rail ne se ferme pas.
              tablette ? 'hidden' : '',
            ].join(' ')}
          >
            <IconeCroix taille={18} />
          </button>
        </div>

        {/*
          * Le bouton de repli, hors de l'en-tête et sur toute la largeur.
          *
          * Dans l'en-tête, il aurait fallu le loger à côté du logo quand la
          * barre est repliée à 56 px : les deux ne tiennent pas. Sur sa propre
          * ligne, il reste au même endroit dans les deux états — ce qui compte
          * pour un bouton qu'on cherche du regard après avoir replié.
          */}
        <button
          type="button"
          onClick={basculerBarre}
          aria-expanded={!barreRepliee}
          aria-label={barreRepliee ? 'Déplier le menu' : 'Replier le menu'}
          className={[
            'items-center gap-2 border-b border-[var(--color-border)] px-3 py-2',
            'text-[12px] font-medium text-[var(--color-fg-muted)]',
            'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
            'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            /*
             * Un seul utilitaire d'affichage à la fois.
             *
             * `hidden` et `flex` posés ensemble laissent l'ordre des règles
             * décider — et il n'est pas garanti. Le bouton avait disparu du
             * grand écran, là où il sert.
             *
             * Replier n'a de sens qu'au-delà de 1024 px : en dessous, la barre
             * est déjà un rail ou un tiroir.
             */
            bureau ? 'flex' : 'hidden',
            replieEffectif ? 'justify-center' : '',
          ].join(' ')}
        >
          <IconeMenu taille={16} />
          {!replieEffectif && <span>Replier le menu</span>}
        </button>

        {/* Le sélecteur de classe se masque lui-même sous deux classes : la
            question ne se pose qu'à qui en suit plusieurs. */}
        {/*
          * Masqués une fois la barre repliée : un `<select>` ne tient pas dans
          * 56 px, et le rendre minuscule reviendrait à le rendre inutilisable
          * plutôt qu'absent. Déplier le menu les ramène — c'est un clic, pour
          * des réglages qu'on touche quelques fois par trimestre.
          */}
        <div
          className={[
            'border-b border-[var(--color-border)] px-3 py-2.5 empty:hidden print:hidden',
            replieEffectif ? 'hidden' : '',
          ].join(' ')}
        >
          <SelecteurClasse />
        </div>

        {/* L'étiquette appartient au <nav>, pas à l'<aside> : ce dernier a le
            rôle « complementary », et un lecteur d'écran cherchant les zones de
            navigation ne le trouverait pas. */}
        <nav
          aria-label="Navigation principale"
          className={[
            'flex-1 overflow-y-auto py-3',
            replieEffectif ? 'px-1.5' : 'px-2.5',
          ].join(' ')}
        >
          {/*
            * Les modules pédagogiques n'apparaissent que pour qui a une classe.
            *
            * Un compte de direction administre les comptes et répartit les
            * classes ; il ne fait pas classe. Lui afficher « Cahier journal »
            * et « Emploi du temps » ouvrirait des écrans vides, puisque le
            * serveur ne lui reconnaît aucune classe de travail — une porte qui
            * ne mène nulle part est pire qu'une absence de porte.
            *
            * Un directeur qui enseigne — le cas courant en primaire —
            * s'affecte une classe et les retrouve aussitôt.
            */}
          <ul className="flex flex-col gap-0.5">
            {(aUneClasse ? MODULES : []).map((m) => (
              <LienModule key={m.id} module={m} surNavigation={fermer} replie={replieEffectif} />
            ))}
          </ul>

          {aUneClasse && (
            <>
              <TitreSection replie={replieEffectif}>Annexe</TitreSection>
              <ul className="flex flex-col gap-0.5">
                {ANNEXE.map((m) => (
                  <LienModule key={m.id} module={m} surNavigation={fermer} replie={replieEffectif} />
                ))}
              </ul>
            </>
          )}

          <TitreSection replie={replieEffectif}>Administration</TitreSection>
          <ul className="flex flex-col gap-0.5">
            {ADMINISTRATION
              /*
               * « Équipe » n'apparaît que si l'offre vend plusieurs comptes.
               * Sur une offre individuelle, l'écran s'ouvrait normalement et
               * laissait inviter un collègue — jusqu'au refus du quota, après
               * la saisie de l'adresse. Proposer une porte qui se referme est
               * pire que ne pas la montrer.
               */
              .filter((m) => m.id !== 'equipe' || equipeIncluse)
              .map((m) => (
                <LienModule key={m.id} module={m} surNavigation={fermer} replie={replieEffectif} />
              ))}
          </ul>
        </nav>

        <div
          className={[
            'flex flex-col gap-3 border-t border-[var(--color-border)] py-3',
            replieEffectif ? 'px-2' : 'px-3',
          ].join(' ')}
        >
          {/*
            * ── Documentation et support, EN BAS ────────────────────────────
            * Au-dessus des réglages d'affichage et de la déconnexion, sous les
            * modules : ce n'est pas là qu'on travaille, mais c'est là qu'on
            * revient quand on est coincé. Les mettre dans la liste des modules
            * les ferait concurrencer le cahier journal ; les cacher dans un
            * menu obligerait à les chercher au moment où l'on est déjà agacé.
            */}
          <ul className="flex flex-col gap-0.5">
            <LienModule
              module={{ id: 'documentation', chemin: '/documentation', libelle: 'Documentation' }}
              surNavigation={fermer}
              replie={replieEffectif}
            />
            {/*
              * Le support s'adresse à qui ADMINISTRE le compte de l'école.
              *
              * L'entrée disparaît pour les autres au lieu de mener à une porte
              * fermée : proposer un écran qui refuse est une petite trahison,
              * et la documentation, elle, reste ouverte à tout le monde
              * — juste au-dessus.
              *
              * Affiché tant que `/auth/me` n'a pas répondu, comme le reste du
              * menu : masquer par défaut ferait clignoter la barre.
              */}
            {(!moi.donnees || moi.donnees.role !== 'MEMBER') && (
              <LienModule
                module={{ id: 'support', chemin: '/support', libelle: 'Support' }}
                surNavigation={fermer}
                replie={replieEffectif}
              />
            )}
          </ul>

          <div className={replieEffectif ? 'hidden' : ''}>
            <ReglagesAffichage />
          </div>

          {/*
            * ── QUI est connecté, juste au-dessus de la déconnexion ──────────
            * L'application n'annonçait rien. Dans une salle des maîtres, où
            * l'ordinateur reste allumé entre deux services, on ne savait pas à
            * qui appartenait la session ouverte — et une séance saisie dans le
            * cahier journal d'un collègue est une correction fastidieuse.
            *
            * Sa place est ici : le regard qui cherche « ce n'est pas mon
            * compte » va au bouton de déconnexion, et trouve le nom sur le
            * chemin.
            *
            * En barre repliée, le nom disparaît mais l'infobulle le porte :
            * cinquante-six pixels ne tiennent pas un nom, et le tronquer en
            * « Cla… » ne renseigne personne.
            */}
          {moi.donnees && (
            <IdentiteConnectee
              /*
               * Repli sur l'adresse si le champ manque.
               *
               * Le serveur le rend toujours — mais pendant une mise en
               * production, un navigateur qui a gardé le nouveau code peut
               * encore parler à l'ancienne API. Une page blanche pour un nom
               * absent serait un incident hors de proportion avec l'enjeu.
               */
              nom={moi.donnees.displayName || moi.donnees.email || ''}
              email={moi.donnees.email ?? ''}
              replie={replieEffectif}
              modifiable={peutSeRenommer}
              surModification={() => setNomOuvert(true)}
            />
          )}


          <button
            type="button"
            onClick={() => void deconnecter()}
            aria-label="Se déconnecter"
            title="Se déconnecter"
            className={[
              'flex items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-medium',
              replieEffectif ? 'justify-center' : '',
              'text-[var(--color-fg-muted)] transition-colors duration-150',
              'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]',
            ].join(' ')}
          >
            <IconeDeconnexion taille={17} />
            <span className={replieEffectif ? 'hidden' : ''}>Se déconnecter</span>
          </button>
        </div>
      </aside>

      {/* Voile du panneau mobile. `aria-hidden` : ce n'est qu'une surface de
          clic — la fermeture reste accessible par Échap et par la croix. */}
      {panneauOuvert && (
        <div
          aria-hidden="true"
          onClick={fermer}
          className={`fixed inset-0 z-30 bg-[var(--color-overlay)] ${tablette ? "hidden" : ""}`}
        />
      )}

      {/* ── Contenu ───────────────────────────────────────────────────────── */}
      <div className={tablette ? (replieEffectif ? 'pl-14' : 'pl-60') : ''}>
        {/*
          * Le bandeau de la plateforme, AVANT tout le reste de la colonne.
          *
          * Dans la colonne de contenu et non en travers de l'écran : la barre
          * latérale est fixée sur toute la hauteur, et un bandeau pleine
          * largeur passerait dessous. Il pousse donc l'en-tête et la page vers
          * le bas, ce qui est exactement ce qu'on veut d'une annonce.
          */}
        {moi.donnees?.impersonation && <BandeauImitation espace={moi.donnees.tenantName} />}
        <BandeauInfo />
        <BandeauSieges direction={peutSeRenommer} />
        <header className={[
          'sticky top-0 z-20 items-center gap-3 border-b border-[var(--color-border)]',
          'bg-[var(--color-surface)] px-4 py-2.5 print:hidden',
          // Un seul utilitaire d'affichage : `hidden` et `flex` ensemble
          // laisseraient l'ordre des règles décider. Dès 768 px la barre est
          // visible, et cet en-tête — qui ne porte que le bouton d'ouverture —
          // n'a plus d'objet.
          tablette ? 'hidden' : 'flex',
        ].join(' ')}>
          <button
            type="button"
            onClick={() => setPanneauOuvert(true)}
            aria-label="Ouvrir le menu"
            aria-expanded={panneauOuvert}
            className="rounded-md p-1.5 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)]"
          >
            <IconeMenu taille={20} />
          </button>
          <span className="text-sm font-semibold">Journal de Classe</span>
        </header>

        {/*
          * Le zoom porte sur le CONTENU, pas sur la coquille.
          *
          * Grossir la barre latérale et l'en-tête réduirait la place du
          * tableau — l'inverse du but recherché. `zoom` plutôt qu'une mise à
          * l'échelle par transformation : celle-ci laisse l'élément occuper sa
          * taille d'origine dans le flux, ce qui produit des blancs et des
          * débordements. `zoom` recalcule la mise en page.
          */}
        <main
          id="contenu"
          className={[
            'mx-auto px-4 py-6 lg:py-8',
            // Un écran de tableau prend tout ; un formulaire garde sa largeur
            // de lecture. Les marges latérales se resserrent aussi sur les
            // premiers : 64 px de rembourrage sur un tableau qui déborde déjà,
            // c'est une colonne de moins.
            ecranLarge(emplacement.pathname)
              ? 'w-full lg:px-5'
              : 'max-w-[84rem] lg:px-8',
          ].join(' ')}
          style={{ zoom: 'var(--zoom-contenu, 1)' }}
        >
          <Outlet />
        </main>
      </div>

      {/* Réglage de taille : flottant, hors du flux et hors impression. */}
      <ReglageZoom />

      {/*
        * Le dialogue vit AU NIVEAU DE LA COQUILLE, pas dans la barre latérale.
        * Sur téléphone, la barre est un panneau qui se referme à chaque
        * navigation : le dialogue disparaîtrait avec elle, au milieu d'une
        * saisie.
        */}
      {moi.donnees && (
        <DialogueMonNom
          ouvert={nomOuvert}
          prenomInitial={moi.donnees.firstName ?? ''}
          nomInitial={moi.donnees.lastName ?? ''}
          surFermeture={() => setNomOuvert(false)}
          surEnregistrement={async (prenom, nomFamille) => {
            await api.requete('/auth/me', {
              methode: 'PATCH',
              corps: { firstName: prenom, lastName: nomFamille },
            });
            // Rechargé plutôt que recopié localement : la barre affiche le
            // `displayName` calculé par le serveur, et le recomposer ici ferait
            // deux règles de repli à tenir d'accord.
            await moi.recharger();
          }}
        />
      )}

      {/* Pied de page d'impression : invisible à l'écran, répété sur chaque
          feuille. Une page retrouvée seule sur un bureau doit dire d'où elle
          vient. */}
      {/*
          Les niveaux figurent au pied comme ils figuraient dans le titre de
          chaque impression de l'application d'origine : dans une salle des
          maîtres, deux cahiers journaux de la même semaine ne se distinguent
          que par la classe.
        */}
      <p className="pied-impression">
        {['Journal de Classe', niveaux, annee.donnees?.label]
          .filter(Boolean).join(' · ')}
        {` · imprimé le ${new Date().toLocaleDateString('fr-FR')}`}
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
/**
 * L'espace supprimé, vu APRÈS reconnexion : le réactiver, ou consulter sa
 * facturation.
 *
 * ── Réactiver suppose un abonnement réglé ───────────────────────────────────
 * Dans le délai de grâce, l'abonnement résilié court encore jusqu'au terme de
 * sa période : la réactivation le rétablit. S'il a pris fin, le serveur répond
 * « paiement requis » — l'écran le dit, et la facturation est juste en
 * dessous.
 *
 * ── La facturation reste ouverte ────────────────────────────────────────────
 * Ses factures, son abonnement : un client qui ferme son espace n'en perd pas
 * pour autant la trace de ce qu'il a payé.
 */
function ReactivationEspace({ purgeAfter, peutAnnuler }: {
  purgeAfter: string;
  peutAnnuler: boolean;
}) {
  const { api, deconnecter } = useSession();
  const { executer, enCours, erreur } = useEcriture();
  const {
    executer: exporterAvantPurge, enCours: exportEnCours, erreur: erreurExport,
  } = useEcriture();

  const date = new Date(purgeAfter).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const joursRestants = Math.max(
    0,
    Math.ceil((new Date(purgeAfter).getTime() - Date.now()) / 86_400_000),
  );

  async function reactiver() {
    const ok = await executer(() => api.requete('/rgpd/deletion/cancel', { methode: 'POST' }));
    // Rechargement complet : l'espace vient de redevenir résoluble, et toute
    // l'application repart du bon pied.
    if (ok) globalThis.location?.reload();
  }

  return (
    <main id="contenu" className="mx-auto flex max-w-[60rem] flex-col gap-5 px-5 py-10">
      <Carte titre="Votre espace est supprimé">
        {erreur && <div className="mb-3"><Message ton="attention">{erreur}</Message></div>}

        <p className="text-[14px] leading-relaxed">
          Vos données seront <strong>définitivement effacées le {date}</strong>,
          {` dans ${joursRestants} jour${joursRestants > 1 ? 's' : ''}`}. D’ici
          là, elles sont intactes et l’espace peut être réactivé.
        </p>

        {peutAnnuler ? (
          <>
            <p className="mt-4">
              <Bouton variante="primary" chargement={enCours} onClick={() => void reactiver()}>
                Réactiver l’espace
              </Bouton>
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
              La réactivation suppose un abonnement réglé. S’il a pris fin,
              souscrivez depuis la facturation ci-dessous, puis réactivez.
            </p>
          </>
        ) : (
          /*
           * Un enseignant d'une école dont la direction a demandé la fermeture.
           * Il a le droit de SAVOIR — c'est son travail de l'année — mais la
           * décision ne lui appartient pas.
           */
          <Message ton="attention" titre="La décision appartient au propriétaire de l’espace">
            Seule la personne qui administre le compte de l’école peut réactiver
            l’espace. Prévenez-la sans tarder si c’est une erreur.
          </Message>
        )}

        {/*
          * L'export reste ouvert jusqu'à la purge : le courriel de suppression
          * le promet, et le droit à la portabilité ne s'éteint pas avec la
          * demande. La direction emporte l'école, un enseignant ses données.
          */}
        <p className="mt-4">
          <Bouton
            variante="secondary"
            chargement={exportEnCours}
            onClick={() => void exporterAvantPurge(() => api.telecharger('/rgpd/export.csv', 'export-journal-de-classe.csv'))}
          >
            {peutAnnuler ? 'Exporter les données de l’école' : 'Exporter mes données'}
          </Bouton>
        </p>
        {erreurExport && <div className="mt-3"><Message ton="attention">{erreurExport}</Message></div>}

        <p className="mt-4">
          <Bouton variante="secondary" onClick={() => void deconnecter()}>
            Se déconnecter
          </Bouton>
        </p>
      </Carte>

      {peutAnnuler && <Abonnement />}
    </main>
  );
}

/**
 * L'abonnement n'est pas réglé : la facturation et l'export, rien d'autre.
 *
 * Le serveur refuse tout module en « paiement requis », lecture comprise, et le
 * vérifie à chaque requête — donc à chaque connexion. L'écran ne montre alors
 * que ce qui reste ouvert : de quoi régler, et de quoi récupérer ses données,
 * qu'on ne retient pas contre un paiement.
 */
function AccesSuspendu({ direction }: { direction: boolean }) {
  const { api, deconnecter } = useSession();
  const { executer, enCours, erreur } = useEcriture();
  /*
   * POURQUOI l'espace est fermé : un abonnement résilié (ou rétracté) n'est
   * pas un impayé. Lui écrire « n'est pas réglé » et « tout revient dès le
   * paiement enregistré » parlait d'une dette qui n'existe pas.
   */
  const abonnement = useDonnees<{ status: string }>('/billing/subscription');
  const statut = abonnement.donnees?.status;
  const texte = statut === 'CANCELED'
    ? {
      titre: 'Votre abonnement a pris fin',
      corps: 'Seules la facturation et l’export de vos données restent accessibles. '
        + 'Vos données sont intactes : reprenez votre abonnement pour retrouver l’accès complet.',
    }
    : statut === 'ESSAI_EXPIRE' || statut === 'AUCUN'
      ? {
        titre: 'Votre période d’essai est terminée',
        corps: 'Seules la facturation et l’export de vos données restent accessibles. '
          + 'Vos données sont intactes : choisissez une offre pour retrouver l’accès complet.',
      }
      : {
        titre: 'L’abonnement de votre espace n’est pas réglé',
        corps: 'Tant qu’il ne l’est pas, seules la facturation et l’export de vos '
          + 'données restent accessibles. Vos données sont intactes : tout revient '
          + 'dès le paiement enregistré.',
      };

  return (
    <main id="contenu" className="mx-auto flex max-w-[60rem] flex-col gap-5 px-5 py-10">
      <Carte titre={texte.titre}>
        <div className="flex flex-col gap-3 text-[14px] leading-relaxed">
          {erreur && <Message ton="erreur">{erreur}</Message>}
          <p>{texte.corps}</p>
          {!direction && (
            <Message ton="info">
              L’abonnement relève de la personne qui administre le compte de
              l’école : prévenez-la.
            </Message>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Bouton
              variante="secondary"
              chargement={enCours}
              onClick={() => void executer(() => api.telecharger('/rgpd/export.csv', 'export-journal-de-classe.csv'))}
            >
              {direction ? 'Exporter les données de l’école' : 'Exporter mes données'}
            </Bouton>
            <Bouton variante="secondary" onClick={() => void deconnecter()}>
              Se déconnecter
            </Bouton>
          </div>
        </div>
      </Carte>

      {direction && <Abonnement />}
    </main>
  );
}
